import CancelIcon from '@mui/icons-material/Cancel';
import { Avatar, Box, IconButton, Tooltip, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import { getInitials } from '../utils/getInitials';

const REMOVE_ICON_COLOR = '#E8765A';

interface RemovableAnnotatorTagProps {
  name: string;
  /** When provided, a removable cross icon is shown on the avatar. */
  onRemove?: () => void;
  /** When true, the cross is shown but non-interactive (grayed out). */
  disabled?: boolean;
  /** Tooltip shown on the disabled cross. */
  disabledTooltip?: string;
}

export const RemovableAnnotatorTag = ({
  name,
  onRemove,
  disabled,
  disabledTooltip,
}: RemovableAnnotatorTagProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  const showCross = Boolean(onRemove) || disabled;

  const crossButton = showCross ? (
    <IconButton
      size="small"
      aria-label={formatMessage({ id: 'admin-common.peopleSection.removePerson' }, { name })}
      onClick={disabled ? undefined : onRemove}
      disabled={disabled}
      sx={{
        position: 'absolute',
        top: -6,
        right: -6,
        padding: 0,
        color: disabled ? theme.palette.action.disabled : REMOVE_ICON_COLOR,
        backgroundColor: theme.palette.background.paper,
        '&:hover': { backgroundColor: theme.palette.background.paper },
        '&.Mui-disabled': { backgroundColor: theme.palette.background.paper },
      }}
    >
      <CancelIcon sx={{ fontSize: 18 }} />
    </IconButton>
  ) : null;

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <Tooltip title={name} arrow>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            fontSize: 14,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          }}
        >
          {getInitials(name)}
        </Avatar>
      </Tooltip>
      {disabled && disabledTooltip ? (
        <Tooltip title={disabledTooltip} arrow placement="top">
          <span>{crossButton}</span>
        </Tooltip>
      ) : (
        crossButton
      )}
    </Box>
  );
};
